import React, {Suspense, useEffect, useRef, useState} from "react";
import {findPageName} from './support/pageTitles';
import {BrowserRouter, Link, Navigate, Route, Routes, useLocation} from "react-router-dom";
import Spinner from "./components/Spinner";
import {
    ChevronDownIcon, CompassIcon, GridIcon, HomeIcon, LightbulbIcon,
    LockIcon, MenuIcon, SettingsIcon, ShirtIcon, SparklesIcon,
} from './components/icons';
import {getConfig} from "./support/Config";
import {FeatureKey, isFeatureEnabled} from "./config/featureFlags";
import NotFound from "./pages/NotFound";
import Home from "./pages/Home";
import ProfileLayout from "./pages/profile/ProfileLayout";
import ProfileInfoPage from "./pages/profile/ProfileInfoPage";
import ProfileAiPage from "./pages/profile/ProfileAiPage";
import ProfilePreferencesPage from "./pages/profile/ProfilePreferencesPage";
import ProfileAccessPage from "./pages/profile/ProfileAccessPage";
import ProfileSecurityPage from "./pages/profile/ProfileSecurityPage";
import ProfileApiKeysPage from "./pages/profile/ProfileApiKeysPage";
import ProfileBackgroundTasksPage from "./pages/profile/ProfileBackgroundTasksPage";
import ProfilePrivacyPage from "./pages/profile/ProfilePrivacyPage";
import Login from "./pages/Login";
import OnboardingPage from "./pages/OnboardingPage";
import LandingPage from "./pages/LandingPage";
import LegalDocumentPage from "./pages/LegalDocumentPage";
import {LEGAL_DOCUMENTS, legalPath} from "./legal";
import LegalReconsentDialog from "./components/legal/LegalReconsentDialog";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import UserAvatar from "./components/UserAvatar";
import Logo from "./components/Logo";
import AdminPanel from "./pages/AdminPanel";
import ClothingList from "./pages/ClothingList";
import ClothingListsPage from "./pages/ClothingListsPage";
import ClothingListDetailPage from "./pages/ClothingListDetailPage";
import ClothingItemDetail from "./pages/ClothingItemDetail";
import ClothingItemEdit from "./pages/ClothingItemEdit";
import ItemDetailsTab from "./pages/ItemDetailsTab";
import ItemOutfitsTab from "./pages/ItemOutfitsTab";
import ItemHistoryTab from "./pages/ItemHistoryTab";
import ItemAiTab from "./pages/ItemAiTab";
import ItemCommentsTab from "./pages/ItemCommentsTab";
import AiRecognitionRequestsPage from "./pages/AiRecognitionRequestsPage";
import FootballCollectionPage from "./pages/FootballCollectionPage";
import FootballItemDetail from "./pages/FootballItemDetail";
import Insights from "./pages/Insights";
import WardrobeValuePage from "./pages/WardrobeValuePage";
import RecommendationsPage from "./pages/RecommendationsPage";
import DisposalCenterPage from "./pages/DisposalCenterPage";
import OutfitPage from "./pages/OutfitPage";
import OutfitBuilderPage from "./pages/OutfitBuilderPage";
import OutfitDetailPage from "./pages/OutfitDetailPage";
import SavedOutfitsPage from "./pages/SavedOutfitsPage";
import StrategyPage from "./pages/StrategyPage";
import ShoppingPage from "./pages/ShoppingPage";
import OutfitEvaluationPage from "./pages/OutfitEvaluationPage";
import PurchaseAnalysisPage from "./pages/PurchaseAnalysisPage";
import IndexingPage from "./pages/IndexingPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import NotificationsPage from "./pages/NotificationsPage";
import TripListPage from "./pages/TripListPage";
import TripFormPage from "./pages/TripFormPage";
import TripDetailPage from "./pages/TripDetailPage";
import {StylistPage} from "./routes/lazyPages";
import UpgradePrompt from "./components/UpgradePrompt";
import CookieBanner from "./components/privacy/CookieBanner";
import CalendarOAuthCallbackPage from "./pages/CalendarOAuthCallbackPage";
import ConsentGate from "./components/ConsentGate";
import {WardrobeProvider} from "./context/WardrobeContext";
import {ToastProvider} from "./context/ToastContext";
import authService from "./service/AuthService";
import {fetchCachedSubscription} from "./service/subscriptionCache";
import {WardrobeSwitcher} from "./components/WardrobeSwitcher";
import {ThemeToggle} from "./components/ThemeToggle";
import NotificationBell from "./components/NotificationBell";
import useDialogA11y from "./hooks/useDialogA11y";
import logger from "./utils/logger";

interface NavItem {
    label: string;
    key: string;
    /** Если задан — пункт виден только при включённом feature-флаге. */
    feature?: FeatureKey;
    /**
     * Раздел открывается только premium-аналитикой (R17). На Free пункт остаётся видимым,
     * но помечается замком и подписью «PRO» — иначе пользователь узнаёт о гейте только
     * по пустому экрану с ошибкой.
     */
    premium?: boolean;
}

interface NavSection {
    id: string;
    label: string;
    icon: React.ReactNode;
    items: NavItem[];
    /** Если задан — весь раздел виден только при включённом feature-флаге. */
    feature?: FeatureKey;
}

interface TopLevelItem {
    label: string;
    key: string;
    icon: React.ReactNode;
}

/**
 * Разделы меню сгруппированы по задачам человека, а не по технологии (F12). Прежняя раскладка
 * разводила близкие задачи по «AI», «Образам» и «Планированию»: рекомендации лежали рядом с
 * распознаванием фото, а разбор гардероба — отдельно от корзины, хотя это одна работа.
 *
 * «Выбытие» — один пункт вместо трёх («Корзина», «Разбор гардероба», «Очередь на выбытие»):
 * внутри у него стадии одного процесса, и выбирать между ними в меню было нечего.
 */
const NAV_SECTIONS: NavSection[] = [
    {
        id: 'wardrobe',
        label: 'Гардероб',
        icon: <ShirtIcon className="w-5 h-5"/>,
        items: [
            {label: 'Все вещи', key: '/clothing'},
            {label: 'Списки', key: '/lists'},
            {label: 'Выбытие', key: '/disposal'},
            {label: 'Поездки', key: '/trips'},
        ],
    },
    {
        id: 'outfits',
        label: 'Образы',
        icon: <SparklesIcon className="w-5 h-5"/>,
        items: [
            {label: 'Образ дня', key: '/outfit'},
            {label: 'Каталог образов', key: '/outfits/saved'},
            {label: 'Оценка образа', key: '/outfit-evaluation'},
        ],
    },
    {
        id: 'assistant',
        label: 'Помощник',
        icon: <LightbulbIcon className="w-5 h-5"/>,
        items: [
            {label: 'AI-стилист', key: '/stylist'},
            {label: 'Рекомендации', key: '/recommendations'},
            {label: 'Список покупок', key: '/shopping'},
            {label: 'Анализ покупки', key: '/purchase-analysis'},
        ],
    },
    {
        id: 'analytics',
        label: 'Аналитика',
        icon: <GridIcon className="w-5 h-5"/>,
        items: [
            {label: 'Аналитика', key: '/insights'},
            {label: 'Стоимость', key: '/wardrobe-value', premium: true},
            {label: 'Стратегия', key: '/strategy'},
        ],
    },
    {
        id: 'service',
        label: 'Сервис',
        icon: <CompassIcon className="w-5 h-5"/>,
        items: [
            {label: 'AI распознавание', key: '/ai-recognition'},
            {label: 'Индексация', key: '/indexing'},
            {label: 'Футбольная форма', key: '/football'},
        ],
    },
];

// Меню с учётом feature-флагов: скрываем разделы и пункты неготовых фич.
const VISIBLE_NAV_SECTIONS: NavSection[] = NAV_SECTIONS
    .filter(s => !s.feature || isFeatureEnabled(s.feature))
    .map(s => ({...s, items: s.items.filter(i => !i.feature || isFeatureEnabled(i.feature))}))
    .filter(s => s.items.length > 0);

function findActiveSection(pathname: string): string | null {
    const base = '/' + pathname.split('/')[1];
    for (const s of NAV_SECTIONS) {
        if (s.items.some(i => i.key === base || i.key === pathname || pathname.startsWith(i.key + '/'))) {
            return s.id;
        }
    }
    return null;
}

/**
 * Публичные экраны контура авторизации: они верстают свой шелл сами (шапка, ценностная
 * колонка, карточка формы) и не должны получать сайдбар и служебный футер приложения.
 */
const AUTH_SCREEN_PATHS = ['/login', '/register', '/verify-email', '/forgot-password', '/reset-password'];

const TOP_ITEMS: TopLevelItem[] = [
    {label: 'Главная', key: '/home', icon: <HomeIcon className="w-5 h-5"/>},
];

function SidebarNav({collapsed, onNavigated, isAdmin, premiumAnalytics, navLabel, navTestId}: {
    collapsed: boolean;
    /**
     * Вызывается после перехода по пункту меню. Сам переход делает `Link` (QA-16:
     * навигация должна быть ссылками, а не `div` с `onClick`), а колбэк нужен
     * мобильной шторке — закрыть себя после выбора пункта.
     */
    onNavigated?: () => void;
    isAdmin: boolean;
    /** Открыта ли premium-аналитика текущим тарифом: от неё зависит замок на пунктах меню. */
    premiumAnalytics: boolean;
    /** Имя landmark-а: сайдбар и мобильная шторка не должны выглядеть как два одинаковых `nav`. */
    navLabel: string;
    /** Локатор landmark-а для тестов: два экземпляра меню различаются по нему (locators.md §5). */
    navTestId: string;
}) {
    const location = useLocation();
    const pathname = location.pathname;
    const activeSection = findActiveSection(pathname);
    const base = '/' + pathname.split('/')[1];

    const storedKey = 'sidebarOpenSections';
    const getStored = (): string[] => {
        try { return JSON.parse(localStorage.getItem(storedKey) || '[]'); } catch { return []; }
    };

    const [openSections, setOpenSections] = useState<string[]>(() => {
        const stored = getStored();
        if (activeSection && !stored.includes(activeSection)) {
            return [...stored, activeSection];
        }
        return stored.length > 0 ? stored : (activeSection ? [activeSection] : []);
    });

    useEffect(() => {
        if (activeSection && !openSections.includes(activeSection)) {
            setOpenSections(prev => [...prev, activeSection]);
        }
    }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        localStorage.setItem(storedKey, JSON.stringify(openSections));
    }, [openSections]);

    const toggleSection = (id: string) => {
        setOpenSections(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const isItemActive = (key: string) =>
        base === key || pathname === key || (key !== '/' && pathname.startsWith(key + '/'));

    return (
        <nav aria-label={navLabel} data-testid={navTestId} className="flex-1 px-2 py-4 overflow-y-auto no-scrollbar space-y-0.5">
            {/* Top-level items */}
            {TOP_ITEMS.map(item => (
                <Link
                    key={item.key}
                    to={item.key}
                    data-testid={`nav-item-${item.key}`}
                    className={`sidebar-item ${collapsed ? 'justify-center' : ''} ${isItemActive(item.key) ? 'active' : ''}`}
                    onClick={onNavigated}
                    title={item.label}
                    aria-current={isItemActive(item.key) ? 'page' : undefined}
                    aria-label={collapsed ? item.label : undefined}
                >
                    {item.icon}
                    {!collapsed && <span className="truncate">{item.label}</span>}
                </Link>
            ))}

            {/* Grouped sections */}
            {VISIBLE_NAV_SECTIONS.map(section => {
                const isOpen = !collapsed && openSections.includes(section.id);
                const isSectionActive = section.items.some(i => isItemActive(i.key));

                const headerInner = (
                    <>
                        <div className="flex items-center gap-3 min-w-0">
                            <span className={isSectionActive ? 'text-brand-400' : ''}>{section.icon}</span>
                            {!collapsed && (
                                <span className={`truncate font-medium text-sm ${isSectionActive ? 'text-white' : 'text-gray-300'}`}>
                                    {section.label}
                                </span>
                            )}
                        </div>
                        {!collapsed && (
                            <ChevronDownIcon
                                className={`w-4 h-4 shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                            />
                        )}
                    </>
                );
                const headerClass = `sidebar-item ${collapsed ? 'justify-center' : 'justify-between'} ${isSectionActive && collapsed ? 'active' : ''} cursor-pointer`;

                return (
                    <div key={section.id}>
                        {/* Свёрнутый сайдбар: заголовок раздела ведёт на первый пункт — это
                            переход, значит ссылка. Развёрнутый: заголовок раскрывает список —
                            это кнопка с `aria-expanded`/`aria-controls` (QA-16). */}
                        {collapsed ? (
                            <Link
                                to={section.items[0].key}
                                data-testid={`nav-section-toggle-${section.id}`}
                                className={headerClass}
                                onClick={onNavigated}
                                title={section.label}
                                aria-label={section.label}
                            >
                                {headerInner}
                            </Link>
                        ) : (
                            <button
                                type="button"
                                data-testid={`nav-section-toggle-${section.id}`}
                                className={`${headerClass} w-full text-left`}
                                onClick={() => toggleSection(section.id)}
                                title={section.label}
                                aria-expanded={isOpen}
                                aria-controls={`nav-section-${section.id}`}
                            >
                                {headerInner}
                            </button>
                        )}

                        {isOpen && (
                            <div id={`nav-section-${section.id}`} data-testid={`nav-section-${section.id}`} className="ml-4 pl-3 border-l border-white/10 space-y-0.5 mt-0.5 mb-1">
                                {section.items.map(item => {
                                    // Раздел под premium, тариф его не открывает: пункт остаётся
                                    // рабочим (экран сам объяснит и предложит апгрейд), но заранее
                                    // помечен замком и подписью «PRO» (R17).
                                    const locked = item.premium === true && !premiumAnalytics;
                                    return (
                                        <Link
                                            key={item.key}
                                            to={item.key}
                                            data-testid={`nav-item-${item.key}`}
                                            className={`px-3 py-1.5 rounded-lg cursor-pointer text-sm transition-colors
                                                        flex items-center justify-between gap-2 ${
                                                isItemActive(item.key)
                                                    ? 'bg-brand-500/20 text-brand-300 font-medium'
                                                    : 'text-gray-400 hover:text-gray-100 hover:bg-white/5'
                                            }`}
                                            onClick={onNavigated}
                                            aria-current={isItemActive(item.key) ? 'page' : undefined}
                                        >
                                            <span className="truncate">{item.label}</span>
                                            {locked && (
                                                <span
                                                    className="shrink-0 inline-flex items-center gap-1 text-[11px]
                                                               font-medium text-brand-300"
                                                    data-testid={`nav-premium-${item.key}`}
                                                    title="Доступно на тарифах PRO и FAMILY"
                                                >
                                                    <LockIcon className="w-3 h-3" size={12}/>
                                                    PRO
                                                </span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Settings */}
            <Link
                to="/settings/profile"
                data-testid="nav-item-/settings/profile"
                className={`sidebar-item ${collapsed ? 'justify-center' : ''} ${(isItemActive('/profile') || pathname.startsWith('/settings/profile')) ? 'active' : ''}`}
                onClick={onNavigated}
                title="Настройки"
                aria-label={collapsed ? 'Настройки' : undefined}
                aria-current={(isItemActive('/profile') || pathname.startsWith('/settings/profile')) ? 'page' : undefined}
            >
                <SettingsIcon className="w-5 h-5"/>
                {!collapsed && <span className="truncate">Настройки</span>}
            </Link>

            {/* Admin */}
            {isAdmin && (
                <Link
                    to="/admin"
                    data-testid="nav-item-/admin"
                    className={`sidebar-item ${collapsed ? 'justify-center' : ''} ${isItemActive('/admin') ? 'active' : ''}`}
                    onClick={onNavigated}
                    title="Админ-панель"
                    aria-label={collapsed ? 'Админ-панель' : undefined}
                    aria-current={isItemActive('/admin') ? 'page' : undefined}
                >
                    <SettingsIcon className="w-5 h-5"/>
                    {!collapsed && <span className="truncate">Админ-панель</span>}
                </Link>
            )}
        </nav>
    );
}

/**
 * Область контента: обычные страницы живут внутри `main` с паддингами и
 * ограничением ширины, а страницы, которые верстают свой лендинг-шелл целиком
 * (публичная главная), получают полную ширину и свой собственный `main`.
 */
function ContentArea({bare, children}: { bare: boolean; children: React.ReactNode }) {
    if (bare) {
        return <div className="flex-1 min-w-0">{children}</div>;
    }
    return (
        <main className="flex-1 p-4 md:p-6 lg:p-8 w-full max-w-[1440px] mx-auto">
            {children}
        </main>
    );
}

function App() {
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(localStorage.getItem('sidebarCollapsed') === 'true');
    const [isAdmin, setIsAdmin] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement>(null);
    const burgerRef = useRef<HTMLButtonElement>(null);
    // Premium-аналитика тарифа: нужна меню, чтобы помечать закрытые разделы замком (R17).
    // Пока подписка не загружена — считаем открытой, иначе замок мигал бы на PRO-тарифе.
    const [premiumAnalytics, setPremiumAnalytics] = useState(true);

    const location = useLocation();
    // Публичные экраны контура авторизации (вход, регистрация, подтверждение почты,
    // восстановление пароля) верстают свой шелл сами.
    const isAuthScreen = AUTH_SCREEN_PATHS.some(path => location.pathname.startsWith(path));
    // Публичный лендинг на `/`: доступен всем — и анониму, и вошедшему
    // пользователю — и рисуется без шелла приложения (сайдбар, шапка, служебный
    // футер), как и экраны авторизации.
    const isLanding = location.pathname === '/';
    // Правовые документы — такая же публичная поверхность: читать их нужно до
    // регистрации, и шелл приложения им ни к чему.
    const isLegal = location.pathname.startsWith('/legal');
    // Мастер онбординга — такой же самостоятельный экран: он ведёт человека по шагам, и шелл
    // приложения (сайдбар, шапка) в нём только уводит в сторону.
    const isOnboarding = location.pathname.startsWith('/onboarding');
    const isChromeless = isAuthScreen || isLanding || isLegal || isOnboarding;

    // Мобильная шторка — модальный диалог: Escape закрывает, фокус держится внутри
    // и возвращается на бургер (U14, styleguide §Modals).
    useDialogA11y(mobileMenuRef, () => setMobileMenuOpen(false), {active: mobileMenuOpen});

    const toggleSidebar = () => {
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);
        localStorage.setItem('sidebarCollapsed', newCollapsed.toString());
    };

    useEffect(() => {
        let isInitializing = false;

        const initializeApp = async () => {
            if (isInitializing) return;
            isInitializing = true;
            try {
                await authService.init();
                const isAuth = authService.isAuthenticated();
                if (isAuth) setIsAdmin(authService.hasRole('clothes-admin'));
            } catch (error) {
                logger.error('App: Failed to initialize auth service', {error: (error as Error)?.message});
            } finally {
                setLoading(false);
                isInitializing = false;
            }
        };

        if (!authService.isInitialized) {
            initializeApp();
        } else {
            setLoading(false);
        }
    }, []);

    // Тариф подтягиваем отдельным эффектом, а не внутри инициализации авторизации: та
    // выполняется только при «холодном» старте (`authService.isInitialized === false`),
    // и на повторном монтировании меню осталось бы без замков. Запрос некритичен —
    // при сбое меню просто без пометок, а гейт объяснит сам экран.
    useEffect(() => {
        if (loading || !authService.isAuthenticated()) {
            return undefined;
        }
        let cancelled = false;
        fetchCachedSubscription()
            .then(sub => {
                if (!cancelled) {
                    setPremiumAnalytics(sub.premiumAnalytics !== false);
                }
            })
            .catch(e => logger.debug('App: subscription fetch failed', {error: (e as Error)?.message}));
        return () => {
            cancelled = true;
        };
    }, [loading]);

    if (loading) return <Spinner/>;

    // Имя раздела — из общей таблицы support/pageTitles (её же читает useDocumentTitle):
    // раньше шапка на /notifications и /subscription оставалась без заголовка (U15).
    const pageName = findPageName(location.pathname);

    const guard = (element: React.ReactNode) => {
        if (location.pathname === '/login') return element;
        if (!authService.isAuthenticated()) {
            const current = location.pathname + location.search + location.hash;
            return <Navigate to={`/login?redirectUrl=${encodeURIComponent(current)}`} replace/>;
        }
        return element;
    };

    return (
        <ToastProvider prefetch={!isChromeless}>
        <WardrobeProvider>
            <UpgradePrompt/>
            <CookieBanner/>
            {/* Повторное согласие с правовыми документами (legal-documents §9): только в
                шелле приложения — на `/legal/*` человек как раз читает документ из диалога. */}
            {!isChromeless && authService.isAuthenticated() && <LegalReconsentDialog/>}
            <div className="min-h-screen bg-[#f6f7f9] dark:bg-ink-950">
                <div className="flex">
                    {/* Sidebar desktop */}
                    {!isChromeless && (
                        // Ширину не анимируем: она перекладывает всю страницу на каждом кадре,
                        // а styleguide разрешает анимировать только transform/opacity. Сворачивание —
                        // редкое осознанное действие, ему достаточно мгновенной перестройки.
                        <aside
                            id="app-sidebar"
                            data-testid="app-sidebar"
                            className={`${collapsed ? 'w-20' : 'w-64'} hidden md:flex flex-col shrink-0 bg-ink-900 text-white min-h-screen sticky top-0 h-screen`}>
                            <Logo collapsed={collapsed}/>
                            <SidebarNav
                                collapsed={collapsed}
                                isAdmin={isAdmin}
                                premiumAnalytics={premiumAnalytics}
                                navLabel="Основная навигация"
                                navTestId="app-nav"
                            />
                        </aside>
                    )}

                    {/* Mobile drawer */}
                    {!isChromeless && (
                        // Шторка не прячется через display: тогда переход не проигрывается
                        // вовсе. Закрытая — уехала за левый край, не ловит указатель и
                        // выключена из дерева доступности (`inert` + `aria-hidden`, U14).
                        <div
                            className={`fixed inset-0 z-50 md:hidden ${
                                mobileMenuOpen ? 'visible' : 'invisible pointer-events-none'
                            }`}
                            aria-hidden={!mobileMenuOpen}
                            data-testid="mobile-menu-wrapper"
                            {...(mobileMenuOpen ? {} : {inert: ''})}
                        >
                            <div
                                className={`fixed inset-0 bg-black/50 backdrop-blur transition-opacity duration-panel ease-out ${
                                    mobileMenuOpen ? 'opacity-100' : 'opacity-0'
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                                data-testid="mobile-menu-backdrop"
                            />
                            <div
                                ref={mobileMenuRef}
                                role="dialog"
                                aria-modal="true"
                                aria-label="Меню приложения"
                                tabIndex={-1}
                                id="mobile-menu"
                                data-testid="mobile-menu"
                                className={`fixed left-0 top-0 h-full w-72 bg-ink-900 text-white flex flex-col
                                            outline-none transition-transform duration-panel ease-drawer ${
                                    mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                                }`}
                            >
                                <Logo onNavigated={() => setMobileMenuOpen(false)}/>
                                <SidebarNav
                                    collapsed={false}
                                    onNavigated={() => setMobileMenuOpen(false)}
                                    isAdmin={isAdmin}
                                    premiumAnalytics={premiumAnalytics}
                                    navLabel="Навигация приложения"
                                    navTestId="app-nav-mobile"
                                />
                            </div>
                        </div>
                    )}

                    {/* Main content */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {!isChromeless && (
                            <header
                                data-testid="app-header"
                                className="sticky top-0 z-30 bg-white/80 dark:bg-ink-900/80 backdrop-blur border-b border-gray-100 dark:border-white/5 px-4 md:px-6 h-16 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <button
                                        ref={burgerRef}
                                        type="button"
                                        className="md:hidden inline-flex items-center justify-center min-w-[44px] min-h-[44px]
                                                   rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                        onClick={() => setMobileMenuOpen(true)}
                                        data-testid="mobile-menu-toggle"
                                        aria-label="Открыть меню"
                                        aria-expanded={mobileMenuOpen}
                                        aria-controls="mobile-menu"
                                    >
                                        <MenuIcon className="w-6 h-6"/>
                                    </button>
                                    <button
                                        type="button"
                                        className="hidden md:inline-flex items-center justify-center min-w-[44px] min-h-[44px]
                                                   rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                        onClick={toggleSidebar}
                                        data-testid="sidebar-toggle"
                                        title={collapsed ? "Развернуть" : "Свернуть"}
                                        aria-label={collapsed ? "Развернуть меню" : "Свернуть меню"}
                                        aria-expanded={!collapsed}
                                        aria-controls="app-sidebar"
                                    >
                                        <MenuIcon className="w-5 h-5"/>
                                    </button>
                                    {/* Не заголовок: страничный `h1` уже называет экран, а `h2`
                                        в шапке шёл перед ним и ломал иерархию уровней (QA-16). */}
                                    <div data-testid="app-header-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                                        {pageName}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <WardrobeSwitcher/>
                                    <NotificationBell/>
                                    <ThemeToggle/>
                                    <UserAvatar/>
                                </div>
                            </header>
                        )}

                        {/* Публичные страницы (лендинг, вход) верстают свой `main` и
                            полноширинный шелл сами — обёртку контента для них не рисуем. */}
                        <ContentArea bare={isChromeless}>
                            <Suspense fallback={<Spinner/>}>
                                <Routes>
                                    {/* Лендинг — публичная витрина продукта: авторизованного
                                        пользователя с `/` не выкидываем, лишь меняем CTA на
                                        переход в приложение (см. components/landing/cta.ts). */}
                                    <Route path="/" element={
                                        <LandingPage authenticated={authService.isAuthenticated()}/>
                                    }/>
                                    {/* Публичные правовые документы: доступны без сессии
                                        (2026-08-26_legal-documents, P-1). Неизвестный slug
                                        отдаёт 404 внутри самой страницы. */}
                                    <Route path="/legal/:slug" element={<LegalDocumentPage/>}/>
                                    <Route path="/login" element={
                                        authService.isAuthenticated() ?
                                            <Navigate to="/home" replace/> :
                                            <Login/>
                                    }/>
                                    {/* Публичный контур регистрации и восстановления: авторизованному
                                        пользователю эти экраны не нужны — уводим на главную приложения. */}
                                    <Route path="/register" element={
                                        authService.isAuthenticated() ?
                                            <Navigate to="/home" replace/> :
                                            <RegisterPage/>
                                    }/>
                                    <Route path="/verify-email" element={<VerifyEmailPage/>}/>
                                    <Route path="/forgot-password" element={
                                        authService.isAuthenticated() ?
                                            <Navigate to="/home" replace/> :
                                            <ForgotPasswordPage/>
                                    }/>
                                    <Route path="/reset-password" element={<ResetPasswordPage/>}/>
                                    <Route path="/home" element={guard(<Home/>)}/>
                                    <Route path="/onboarding" element={guard(<OnboardingPage/>)}/>
                                    <Route path="/clothing" element={guard(<ClothingList/>)}/>
                                    {/* Контур выбытия: один раздел со стадиями. Старые адреса —
                                        закладки, deep-link уведомлений, ссылки из чата стилиста —
                                        ведут на соответствующую стадию, а не в 404. */}
                                    <Route path="/disposal" element={guard(<DisposalCenterPage/>)}/>
                                    <Route path="/disposal/:stage" element={guard(<DisposalCenterPage/>)}/>
                                    <Route path="/trash" element={<Navigate to="/disposal/pending" replace/>}/>
                                    <Route path="/disposed" element={<Navigate to="/disposal/archive" replace/>}/>
                                    <Route path="/declutter" element={<Navigate to="/disposal/decisions" replace/>}/>
                                    <Route path="/wardrobes" element={guard(<Navigate to="/clothing" replace/>)}/>
                                    <Route path="/ai-recognition" element={guard(<ConsentGate><AiRecognitionRequestsPage/></ConsentGate>)}/>
                                    <Route path="/ai-recognition/:requestId" element={guard(<ConsentGate><AiRecognitionRequestsPage/></ConsentGate>)}/>
                                    <Route path="/insights" element={guard(<Insights/>)}/>
                                    {/* Legacy deep-link INSIGHT-уведомлений: в БД уже накоплены строки с
                                        несуществующим /wardrobe/insights — редирект вместо 404. */}
                                    <Route path="/wardrobe/insights" element={<Navigate to="/insights" replace/>}/>
                                    <Route path="/wardrobe-value" element={guard(<WardrobeValuePage/>)}/>
                                    <Route path="/stylist" element={guard(<StylistPage/>)}/>
                                    <Route path="/stylist/:conversationId" element={guard(<StylistPage/>)}/>
                                    <Route path="/recommendations" element={guard(<RecommendationsPage/>)}/>
                                    <Route path="/strategy" element={guard(<StrategyPage/>)}/>
                                    <Route path="/indexing" element={guard(<IndexingPage/>)}/>
                                    <Route path="/outfit" element={guard(<OutfitPage/>)}/>
                                    <Route path="/outfits/builder" element={guard(<OutfitBuilderPage/>)}/>
                                    <Route path="/outfit-evaluation" element={guard(<ConsentGate><OutfitEvaluationPage/></ConsentGate>)}/>
                                    <Route path="/purchase-analysis" element={guard(<ConsentGate><PurchaseAnalysisPage/></ConsentGate>)}/>
                                    <Route path="/shopping" element={guard(<ShoppingPage/>)}/>
                                    <Route path="/outfits/saved" element={guard(<SavedOutfitsPage/>)}/>
                                    <Route path="/outfits/saved/:id" element={guard(<OutfitDetailPage/>)}/>
                                    <Route path="/football" element={guard(<FootballCollectionPage/>)}/>
                                    <Route path="/football/:id" element={guard(<FootballItemDetail/>)}/>
                                    <Route path="/clothing/:id" element={guard(<ClothingItemDetail/>)}>
                                        <Route index element={<Navigate to="details" replace/>}/>
                                        <Route path="details" element={<ItemDetailsTab/>}/>
                                        <Route path="outfits" element={<ItemOutfitsTab/>}/>
                                        <Route path="history" element={<ItemHistoryTab/>}/>
                                        <Route path="ai"      element={<ItemAiTab/>}/>
                                        <Route path="comments" element={<ItemCommentsTab/>}/>
                                    </Route>
                                    <Route path="/trips" element={guard(<TripListPage/>)}/>
                                    <Route path="/trips/new" element={guard(<TripFormPage/>)}/>
                                    <Route path="/trips/:id" element={guard(<TripDetailPage/>)}/>
                                    <Route path="/trips/:id/edit" element={guard(<TripFormPage/>)}/>
                                    <Route path="/lists" element={guard(<ClothingListsPage/>)}/>
                                    <Route path="/lists/:id" element={guard(<ClothingListDetailPage/>)}/>
                                    <Route path="/clothing/:id/edit" element={guard(<ClothingItemEdit/>)}/>
                                    <Route path="/profile" element={<Navigate to="/settings/profile" replace/>}/>
                                    <Route path="/profile/privacy" element={<Navigate to="/settings/profile/privacy" replace/>}/>
                                    <Route path="/settings/profile" element={guard(<ProfileLayout/>)}>
                                        <Route index element={<Navigate to="info" replace/>}/>
                                        <Route path="info"        element={<ProfileInfoPage/>}/>
                                        <Route path="ai"          element={<ProfileAiPage/>}/>
                                        <Route path="preferences" element={<ProfilePreferencesPage/>}/>
                                        <Route path="access"      element={<ProfileAccessPage/>}/>
                                        <Route path="security"    element={<ProfileSecurityPage/>}/>
                                        <Route path="api-keys"    element={<ProfileApiKeysPage/>}/>
                                        <Route path="background-tasks" element={<ProfileBackgroundTasksPage/>}/>
                                        <Route path="privacy"     element={<ProfilePrivacyPage/>}/>
                                    </Route>
                                    {/* Возврат из Google OAuth: redirect_uri ведёт сюда, а не на
                                        backend-эндпоинт — тот требует JWT (CTHS-174). */}
                                    <Route path="/calendar/callback" element={guard(<CalendarOAuthCallbackPage/>)}/>
                                    <Route path="/notifications" element={guard(<NotificationsPage/>)}/>
                                    <Route path="/subscription" element={guard(<SubscriptionPage/>)}/>
                                    {isAdmin && (<Route path="/admin" element={guard(<AdminPanel/>)}/>)}
                                    <Route path="/*" element={<NotFound/>}/>
                                </Routes>
                            </Suspense>
                        </ContentArea>

                        {!isChromeless && (
                            <footer
                                className="border-t border-gray-100 dark:border-white/5 px-4 sm:px-6 py-4
                                           text-center text-xs text-gray-400 dark:text-gray-500 min-w-0">
                                <div className="break-words">
                                    ©{new Date().getFullYear()} Created by Forwolk{' '}
                                    <span className="whitespace-nowrap">
                                        ({getConfig("appName")} v. {getConfig("appVersion")})
                                    </span>
                                </div>
                                {/* Правовые документы должны быть достижимы и после входа,
                                    а не только с витрины (2026-08-26_legal-documents, N-2). */}
                                <nav
                                    aria-label="Правовые документы"
                                    className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1"
                                >
                                    {LEGAL_DOCUMENTS.map(document => (
                                        <Link
                                            key={document.slug}
                                            to={legalPath(document)}
                                            className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-ui ease-out"
                                        >
                                            {document.shortTitle}
                                        </Link>
                                    ))}
                                </nav>
                            </footer>
                        )}
                    </div>
                </div>
            </div>
        </WardrobeProvider>
        </ToastProvider>
    );
}

export default function AppWrapper() {
    return (
        <BrowserRouter>
            <App/>
        </BrowserRouter>
    );
}
